import { useEffect, useRef, useCallback } from 'react';
import { useSocketContext } from '../contexts/SocketContext';

/**
 * هوک اصلی برای دسترسی به وضعیت سوکت و متدهای آن
 * 
 * @example
 * const { socket, isConnected, socketId, emit } = useSocket();
 */
export const useSocket = () => {
  return useSocketContext();
};

/**
 * هوک برای گوش دادن به رویدادهای Socket.io با مدیریت خودکار cleanup
 * و جلوگیری از مشکلات Stale Closure و re-render اضافه.
 *
 * @param {string} eventName نام رویدادی که باید شنود شود
 * @param {Function} handler تابعی که هنگام دریافت رویداد فراخوانی می‌شود
 * @param {boolean} [enabled=true] فعال یا غیرفعال بودن شنود رویداد
 * 
 * @example
 * useSocketEvent('new_notification', (data) => {
 *   console.log('Received notification:', data);
 * });
 */
export const useSocketEvent = (eventName, handler, enabled = true) => {
  const { socket } = useSocket();
  const handlerRef = useRef(handler);

  // همیشه آخرین نسخه تابع handler را بدون re-subscribe ذخیره می‌کنیم
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!socket || !eventName || !enabled) return;

    const eventListener = (...args) => {
      if (handlerRef.current) {
        handlerRef.current(...args);
      }
    };

    socket.on(eventName, eventListener);

    return () => {
      socket.off(eventName, eventListener);
    };
  }, [socket, eventName, enabled]);
};

/**
 * هوک کمکی برای ارسال رویداد سوکت با Promise و پشتیبانی از Timeout و Acknowledgment
 *
 * @example
 * const emitWithAck = useSocketEmit();
 * const response = await emitWithAck('order:create', { id: 123 });
 */
export const useSocketEmit = () => {
  const { socket } = useSocket();

  return useCallback(
    (eventName, payload, timeoutMs = 8000) => {
      return new Promise((resolve, reject) => {
        if (!socket) {
          return reject(new Error('Socket is not available'));
        }

        if (!socket.connected) {
          return reject(new Error('Socket is not connected'));
        }

        let isTimedOut = false;
        const timer = setTimeout(() => {
          isTimedOut = true;
          reject(new Error(`Socket emit timeout for event: "${eventName}"`));
        }, timeoutMs);

        socket.emit(eventName, payload, (response) => {
          if (isTimedOut) return;
          clearTimeout(timer);
          resolve(response);
        });
      });
    },
    [socket]
  );
};

export default useSocket;
